/**
 * 模板缩略图：把模板用样例数据渲染成一张小纸片。
 *
 * 用户看名字「报价单」「采购单」猜不出长什么样，看一眼缩略图就知道要不要。
 * 就是成品渲染器 + 预览模式的自适应缩放脚本，塞进一个不可交互的小 iframe，
 * 取顶部那一截 —— 单据的抬头和表头就在那儿，最能代表这张模板。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { buildContractHtml } from '../../../render-core/renderer';
import { buildSampleContractData } from '../../../render-core/sampleData';
import type { FieldMeta } from '../../api/types';
import type { ContractTemplate } from '../../contract/types';

interface Props {
  template: ContractTemplate;
  fieldMetas: FieldMeta[];
  /** 高度 px，宽度撑满容器。 */
  height?: number;
  /** 右下角的小标签，默认「样例」。传 null 不显示。 */
  badge?: string | null;
  /** 悬停出「点击预览」，点了弹整页大图。默认开。 */
  previewable?: boolean;
}

/** A4 竖版纸宽（210mm ≈ 794px）。iframe 固定按这个宽度渲染，再整体缩放到容器宽。 */
const PAPER_PX = 794;

export function TemplateThumb({ template, fieldMetas, height = 120, badge = '样例', previewable = true }: Props) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  // 缩放由外层量出来的容器宽度决定，不靠 iframe 里的脚本 —— 那个脚本在网格列宽
  // 还没定下来时就算了一次（拿到整行宽度 → 缩放 1），之后不会再算，缩略图就是
  // 一角放大的残片。
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.3);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const apply = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / PAPER_PX);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const html = useMemo(() => {
    try {
      return buildContractHtml(template, buildSampleContractData(fieldMetas), 'preview');
    } catch {
      return '';
    }
  }, [template, fieldMetas]);
  return (
    <div
      ref={box}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        if (!previewable) return;
        e.stopPropagation();
        setOpen(true);
      }}
      style={{
        position: 'relative',
        height,
        overflow: 'hidden',
        background: '#fff',
        borderBottom: '1px solid var(--border-soft)',
        cursor: previewable ? 'zoom-in' : 'default',
      }}
    >
      {previewable && hover && (
        <div
          style={{
            position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(42,37,25,.35)', color: '#fff', fontSize: 13, fontWeight: 600, pointerEvents: 'none',
          }}
        >
          🔍 点击预览
        </div>
      )}
      {previewable && open && (
        <Modal open onCancel={() => setOpen(false)} footer={null} width={860} title={template.name} destroyOnClose>
          <div style={{ background: 'var(--canvas-bg)', padding: 16, borderRadius: 8, maxHeight: '75vh', overflowY: 'auto' }} className="pp-scroll">
            <iframe
              title={template.name + ' 预览'}
              srcDoc={html}
              sandbox="allow-scripts"
              style={{ width: PAPER_PX, height: 1123, border: 'none', display: 'block', margin: '0 auto', background: '#fff', boxShadow: '0 8px 24px rgba(50,30,0,.2)' }}
            />
          </div>
        </Modal>
      )}
      {html ? (
        <iframe
          title={template.name}
          srcDoc={html}
          sandbox="allow-scripts"
          tabIndex={-1}
          style={{
            width: PAPER_PX,
            height: height / scale,
            border: 'none',
            pointerEvents: 'none',
            display: 'block',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />
      ) : null}
      {/* 底部渐隐：告诉眼睛「下面还有」，而不是一刀切断 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 28,
          background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,.9))',
          pointerEvents: 'none',
        }}
      />
      {badge && (
        <span
          style={{
            position: 'absolute',
            right: 6,
            bottom: 6,
            fontSize: 10,
            padding: '1px 6px',
            borderRadius: 4,
            background: 'rgba(42,37,25,.7)',
            color: '#f7f3eb',
          }}
        >
          {badge}
        </span>
      )}
    </div>
  );
}
